import React, { memo } from "react";
import cx from "classnames";
import type { IMessage } from "@typing/wsui/getMessages";
import type { ToolClickHandler } from "../../types";
import UserMessage from "../UserMessage";
import AIMessage from "../AIMessage";
import styles from "./index.module.less";

interface IMessageCardProps {
    message: IMessage;
    expandedThinking: Record<string, boolean>;
    onToggleThinking: (segmentKey: string) => void;
    onToolClick: ToolClickHandler;
    sessionId: string;
    onAppendAskClick: (question: string) => void;
    onRetryMessage?: (message: IMessage) => void;
    /** 与上一条消息同侧（角色相同），同侧间距 16，交叉侧间距 24 */
    prevSameRole?: boolean;
    sseLoading?: boolean;
}

/**
 * 消息卡片组件
 * 根据消息角色渲染用户消息或 AI 消息
 */
const MessageCard: React.FC<IMessageCardProps> = ({
    message,
    expandedThinking,
    onToggleThinking,
    onToolClick,
    sessionId,
    onAppendAskClick,
    onRetryMessage,
    prevSameRole = false,
    sseLoading,
}) => {
    const isUser = message.poster.role === "user";
    const wrapperClass = isUser
        ? cx(styles.userMessage, { [styles.sameSide]: prevSameRole })
        : cx(styles.aiMessage, { [styles.sameSide]: prevSameRole });

    return (
        <div className={wrapperClass}>
            {isUser ? (
                <UserMessage message={message} onRetry={onRetryMessage} />
            ) : (
                <AIMessage
                    message={message}
                    expandedThinking={expandedThinking}
                    onToggleThinking={onToggleThinking}
                    onToolClick={onToolClick}
                    sessionId={sessionId}
                    onAppendAskClick={onAppendAskClick}
                    sseLoading={sseLoading}
                />
            )}
        </div>
    );
};

/**
 * 自定义 memo 比较：
 * 1. user message 不依赖 expandedThinking/onToggleThinking，跳过这两个 prop 的比较
 * 2. bot message 只比较自己消息的 thinkProcessV2 segmentKey，
 *    避免其他消息的 expandThinking 调用（引用变化）触发所有历史 bot 消息重渲染
 */
export default memo(MessageCard, (prev, next) => {
    if (prev.message !== next.message) return false;
    if (prev.sseLoading !== next.sseLoading) return false;
    if (prev.onToolClick !== next.onToolClick) return false;
    if (prev.sessionId !== next.sessionId) return false;
    if (prev.onAppendAskClick !== next.onAppendAskClick) return false;
    if (prev.onRetryMessage !== next.onRetryMessage) return false;
    if (next.message.poster.role === "bot") {
        if (prev.onToggleThinking !== next.onToggleThinking) return false;
        // 只比较当前消息自身 thinkProcessV2 段的 expandedThinking key
        // 其他消息的 expandThinking 调用只改变不相干的 key，无需重渲染
        for (const section of next.message.sections) {
            for (const segment of section.segments) {
                if (segment.type === "thinkProcessV2") {
                    const key = `${next.message.message_id}_${segment.segment_idx}`;
                    if (prev.expandedThinking[key] !== next.expandedThinking[key]) return false;
                }
            }
        }
    }
    return true;
});
